import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { Link } from "react-router-dom";
import { PRODUCTS } from "@/data/products";
import { LEGAL_PAGES } from "@/data/legal";
import { api } from "@/lib/api";
import { useTranslation } from "react-i18next";

export default function GlobalSearch() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [news, setNews] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && !news.length) api.get("/news").then((r) => setNews(r.data.items || [])).catch(() => {});
    if (open && !downloads.length) api.get("/downloads").then((r) => setDownloads(r.data.items || [])).catch(() => {});
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open, news.length, downloads.length]);

  useEffect(() => {
    const h = (e) => { if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setOpen(true); } if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, []);

  const results = useMemo(() => {
    if (!q.trim()) return { products: PRODUCTS.slice(0, 3), variants: [], news: news.slice(0, 3), downloads: downloads.slice(0, 3), legal: [] };
    const s = q.toLowerCase();
    const prods = PRODUCTS.filter((p) => p.name.toLowerCase().includes(s) || p.tagline.toLowerCase().includes(s));
    const variants = PRODUCTS.flatMap((p) => p.variants.filter((v) => v.name.toLowerCase().includes(s) || v.note.toLowerCase().includes(s)).map((v) => ({ ...v, product: p })));
    const n = news.filter((x) => x.title.toLowerCase().includes(s) || (x.excerpt || "").toLowerCase().includes(s));
    const d = downloads.filter((x) => x.title.toLowerCase().includes(s) || (x.category || "").toLowerCase().includes(s));
    const l = LEGAL_PAGES.filter((x) => x.title.toLowerCase().includes(s));
    return { products: prods, variants, news: n, downloads: d, legal: l };
  }, [q, news, downloads]);

  return (
    <>
      <button onClick={() => setOpen(true)} className="w-9 h-9 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors" aria-label="Search" data-testid="global-search-toggle">
        <Search className="w-4 h-4" />
      </button>
      {open && (
        <div className="fixed inset-0 z-[80] flex items-start justify-center p-4 pt-24 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} data-testid="global-search-modal">
          <div className="w-full max-w-2xl bg-background rounded-2xl border border-border shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
              <Search className="w-4 h-4 text-muted-foreground" />
              <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("common.search")} className="flex-1 bg-transparent focus:outline-none text-sm" data-testid="global-search-input" />
              <kbd className="hidden sm:inline text-[10px] px-2 py-1 rounded border border-border text-muted-foreground">ESC</kbd>
              <button onClick={() => setOpen(false)} className="w-7 h-7 rounded-full hover:bg-muted flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto p-4 space-y-4">
              {results.products.length > 0 && <Group title="Brands">
                {results.products.map((p) => (
                  <ResultLink key={p.slug} to={`/brands/${p.slug}`} onClick={() => setOpen(false)} testId={`search-result-${p.slug}`}>
                    <div className="w-9 h-9 rounded-md flex items-center justify-center text-white font-bold text-xs" style={{ background: p.accent }}>{p.name.slice(0,2)}</div>
                    <div><div className="font-semibold text-sm">{p.name}</div><div className="text-xs text-muted-foreground">{p.tagline}</div></div>
                  </ResultLink>
                ))}
              </Group>}
              {results.variants.length > 0 && <Group title="Variants">
                {results.variants.slice(0, 6).map((v) => (
                  <ResultLink key={`${v.product.slug}-${v.name}`} to={`/brands/${v.product.slug}`} onClick={() => setOpen(false)}>
                    <div className="w-9 h-9 rounded-md bg-gold-400/15 flex items-center justify-center text-gold-600 font-bold text-xs">{v.product.name.slice(0, 2)}</div>
                    <div><div className="font-semibold text-sm">{v.name}</div><div className="text-xs text-muted-foreground">{v.product.name} · {v.note}</div></div>
                  </ResultLink>
                ))}
              </Group>}
              {results.news.length > 0 && <Group title="News">
                {results.news.slice(0, 5).map((n) => (
                  <ResultLink key={n.id} to="/news" onClick={() => setOpen(false)}>
                    <div className="w-9 h-9 rounded-md bg-muted flex items-center justify-center text-xs">📰</div>
                    <div><div className="font-semibold text-sm">{n.title}</div><div className="text-xs text-muted-foreground">{n.category}</div></div>
                  </ResultLink>
                ))}
              </Group>}
              {results.downloads.length > 0 && <Group title="Downloads">
                {results.downloads.slice(0, 5).map((d) => (
                  <ResultLink key={d.id || d.title} to="/downloads" onClick={() => setOpen(false)}>
                    <div className="w-9 h-9 rounded-md bg-muted flex items-center justify-center text-xs">📄</div>
                    <div><div className="font-semibold text-sm">{d.title}</div><div className="text-xs text-muted-foreground">{d.category} · {d.size}</div></div>
                  </ResultLink>
                ))}
              </Group>}
              {results.legal.length > 0 && <Group title="Legal">
                {results.legal.slice(0, 5).map((l) => (
                  <ResultLink key={l.slug} to={`/legal/${l.slug}`} onClick={() => setOpen(false)}>
                    <div className="w-9 h-9 rounded-md bg-muted flex items-center justify-center text-xs">§</div>
                    <div><div className="font-semibold text-sm">{l.title}</div></div>
                  </ResultLink>
                ))}
              </Group>}
              {q && !results.products.length && !results.variants.length && !results.news.length && !results.downloads.length && !results.legal.length && (
                <div className="text-center py-10 text-sm text-muted-foreground">No results for &ldquo;{q}&rdquo;</div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const Group = ({ title, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-2 px-2">{title}</div>
    <div className="space-y-1">{children}</div>
  </div>
);
const ResultLink = ({ to, onClick, children, testId }) => (
  <Link to={to} onClick={onClick} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-colors" data-testid={testId}>
    {children}
  </Link>
);
